import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./AuthContext";
import { SelectEmpresaDialog, type EmpresaOption } from "@/components/empresa/SelectEmpresaDialog";

interface EmpresaContextType {
  empresaId: number | null;
  isLoading: boolean;
  error: Error | null;
  empresas: EmpresaOption[];
  trocarEmpresa: () => void;
}

const EmpresaContext = createContext<EmpresaContextType>({
  empresaId: null,
  isLoading: true,
  error: null,
  empresas: [],
  trocarEmpresa: () => {},
});

const CACHE_KEY = "app_empresa_id";
const selectedKey = (uid: string) => `selected_empresa_id:${uid}`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

export const EmpresaProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [empresaId, setEmpresaId] = useState<number | null>(null);
  const [empresas, setEmpresas] = useState<EmpresaOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [needsSelection, setNeedsSelection] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const applyEmpresa = useCallback(async (id: number | null, uid?: string) => {
    if (id) {
      // Sincroniza empresa ativa no banco ANTES de setar o state — as queries
      // dependem de users.empresa_id via RLS. Sem sincronizar, o RLS filtra
      // pela última empresa aceita e retorna 0 registros.
      try {
        await supabase.rpc("trocar_empresa_ativa", { p_empresa_id: id });
      } catch (e) {
        console.warn("Falha ao sincronizar empresa ativa:", e);
      }
      localStorage.setItem(CACHE_KEY, String(id));
      if (uid) localStorage.setItem(selectedKey(uid), String(id));
    } else {
      localStorage.removeItem(CACHE_KEY);
    }
    setEmpresaId(id);
  }, []);



  useEffect(() => {
    const run = async () => {
      if (!user?.id) {
        setEmpresaId(null);
        setEmpresas([]);
        setNeedsSelection(false);
        localStorage.removeItem(CACHE_KEY);
        setIsLoading(false);
        setError(null);
        return;
      }

      setIsLoading(true);
      try {
        // 1) Buscar vínculos ativos do usuário
        const { data: vinculos, error: vErr } = await db
          .from("user_empresa_vinculos")
          .select("empresa_id, modelo_acesso, ativo, empresas(nome_fantasia, razao_social, ativo)")
          .eq("user_id", user.id)
          .eq("ativo", true);
        if (vErr) throw vErr;

        // Filtrar só empresas ativas
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const opts: EmpresaOption[] = (vinculos ?? [])
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .filter((v: any) => v.empresas?.ativo !== false)
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .map((v: any) => ({
            empresa_id: Number(v.empresa_id),
            nome: v.empresas?.nome_fantasia?.trim() || v.empresas?.razao_social?.trim() || `Empresa #${v.empresa_id}`,
            modelo_acesso: v.modelo_acesso ?? null,
          }));

        setEmpresas(opts);

        // 2) Fallback para users.empresa_id se não houver vínculos (donos)
        if (opts.length === 0) {
          const { data: rpcData } = await supabase.rpc("get_current_user_empresa_id");
          const fallback = (rpcData as number | null) ?? null;
          applyEmpresa(fallback, user.id);
          setNeedsSelection(false);
          return;
        }

        // 3) Um único vínculo → seleção automática
        if (opts.length === 1) {
          applyEmpresa(opts[0].empresa_id, user.id);
          setNeedsSelection(false);
          return;
        }

        // 4) Múltiplos vínculos → usar seleção anterior se ainda válida
        const stored = localStorage.getItem(selectedKey(user.id));
        const storedId = stored ? Number(stored) : null;
        const match = storedId
          ? opts.find((o) => o.empresa_id === storedId)
          : null;

        if (match) {
          applyEmpresa(match.empresa_id, user.id);
          setNeedsSelection(false);
        } else {
          // Precisa escolher
          setEmpresaId(null);
          localStorage.removeItem(CACHE_KEY);
          setNeedsSelection(true);
        }
      } catch (err) {
        console.error("EmpresaContext:", err);
        setError(err as Error);
      } finally {
        setIsLoading(false);
      }
    };
    run();
  }, [user?.id, applyEmpresa]);

  const handleSelect = async (id: number) => {
    if (!user?.id) return;
    await applyEmpresa(id, user.id);
    setNeedsSelection(false);
  };


  const trocarEmpresa = () => {
    if (empresas.length > 1) {
      setNeedsSelection(true);
      setEmpresaId(null);
      localStorage.removeItem(CACHE_KEY);
    }
  };

  return (
    <EmpresaContext.Provider value={{ empresaId, isLoading, error, empresas, trocarEmpresa }}>
      {children}
      <SelectEmpresaDialog
        open={needsSelection}
        empresas={empresas}
        onSelect={handleSelect}
      />
    </EmpresaContext.Provider>
  );
};

export function useEmpresa() {
  const context = useContext(EmpresaContext);
  if (!context) {
    return {
      empresaId: null,
      isLoading: false,
      error: null,
      empresas: [] as EmpresaOption[],
      trocarEmpresa: () => {},
    };
  }
  return context;
}
